import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

// ─── Constants ────────────────────────────────────────────────────────────────

const EVERYWHERE_LABELS = [
  'EVERYWHERE.',
  'EN TODAS PARTES.',
  'PARTOUT.',
  'どこでも。',
  'في كل مكان.',
  'ÜBERALL.',
];

const ORBIT_WORDS = [
  { word: 'ESPAÑOL',    x: '8%',   y: '12%', opacity: 0.08 },
  { word: 'FRANÇAIS',   x: '82%',  y: '8%',  opacity: 0.09 },
  { word: '日本語',      x: '5%',   y: '55%', opacity: 0.07 },
  { word: 'DEUTSCH',    x: '88%',  y: '45%', opacity: 0.10 },
  { word: '中文',        x: '15%',  y: '85%', opacity: 0.08 },
  { word: 'PORTUGUÊS',  x: '75%',  y: '82%', opacity: 0.09 },
  { word: '한국어',      x: '45%',  y: '6%',  opacity: 0.07 },
  { word: 'ITALIANO',   x: '90%',  y: '70%', opacity: 0.08 },
  { word: 'العربية',    x: '3%',   y: '30%', opacity: 0.09 },
  { word: 'हिन्दी',    x: '60%',  y: '90%', opacity: 0.07 },
  { word: 'РУССКИЙ',    x: '70%',  y: '20%', opacity: 0.08 },
  { word: 'TÜRKÇE',     x: '25%',  y: '70%', opacity: 0.09 },
];

// Waveform bar heights
const BAR_COUNT = 42;
const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) =>
  Math.abs(Math.sin(i * 0.45)) * 0.6 + Math.abs(Math.sin(i * 0.9)) * 0.4,
);

// ─── Canvas convergence animation ─────────────────────────────────────────────

const PARTICLE_COUNT = 200;

function initParticles(W, H) {
  return Array.from({ length: PARTICLE_COUNT }, () => {
    const side = Math.floor(Math.random() * 4);
    let x, y;
    if (side === 0) { x = Math.random() * W; y = 0; }
    else if (side === 1) { x = W; y = Math.random() * H; }
    else if (side === 2) { x = Math.random() * W; y = H; }
    else { x = 0; y = Math.random() * H; }
    const speed = 1.8 + Math.random() * 2.2;
    return { x, y, speed, done: false };
  });
}

function ConvergenceCanvas({ visible }) {
  const canvasRef  = useRef(null);
  const stateRef   = useRef({
    phase: -1,
    startTime: null,
    particles: [],
    waveOffset: 0,
    canvasOpacity: 1,
  });
  const rafRef = useRef(null);
  const mountedRef = useRef(true);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mountedRef.current) return;

    const ctx = canvas.getContext('2d');
    const W   = canvas.width;
    const H   = canvas.height;
    const cx  = W / 2;
    const cy  = H / 2;
    const st  = stateRef.current;
    const now = performance.now();

    if (st.startTime === null) st.startTime = now;
    const elapsed = (now - st.startTime) / 1000; // seconds

    // Determine phase
    let phase;
    if      (elapsed < 2.0) phase = 0;
    else if (elapsed < 3.0) phase = 1;
    else if (elapsed < 4.0) phase = 2;
    else if (elapsed < 4.8) phase = 3;
    else if (elapsed < 5.3) phase = 4; // white flash
    else                     phase = 5; // fade out

    st.phase = phase;

    // Canvas alpha
    let canvasAlpha = 1;
    if (phase === 5) {
      const fadeProgress = Math.min(1, (elapsed - 5.3) / 0.9);
      canvasAlpha = 1 - fadeProgress;
    }

    canvas.style.opacity = canvasAlpha;

    if (canvasAlpha <= 0.01) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    // Clear
    ctx.clearRect(0, 0, W, H);

    // ── Phase 0: Waveforms sliding to center ─────────────────────────────────
    if (phase >= 0) {
      const waveProgress = Math.min(1, elapsed / 2.0);
      const t = waveProgress;

      ctx.save();
      ctx.globalAlpha = Math.min(1, t * 2) * (phase >= 4 ? 0 : 1);

      // Left waveform: starts at x=0, moves right to cx
      const leftEndX = t * cx;
      // Right waveform: starts at x=W, moves left to cx
      const rightStartX = W - t * (W - cx);

      const waveH = H * 0.18;
      const segments = 80;

      // Left wave
      ctx.beginPath();
      for (let i = 0; i <= segments; i++) {
        const px = (i / segments) * leftEndX;
        const wave = Math.sin(i * 0.28 + now * 0.002) * waveH * 0.4
                   + Math.sin(i * 0.55 + now * 0.003) * waveH * 0.25;
        const py = cy + wave;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = 'rgba(96,170,255,0.5)';
      ctx.lineWidth   = 1.5;
      ctx.stroke();

      // Right wave
      ctx.beginPath();
      for (let i = 0; i <= segments; i++) {
        const px = rightStartX + (i / segments) * (W - rightStartX);
        const wave = Math.sin(i * 0.28 + now * 0.002 + Math.PI) * waveH * 0.4
                   + Math.sin(i * 0.55 + now * 0.003 + Math.PI) * waveH * 0.25;
        const py = cy + wave;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = 'rgba(96,170,255,0.5)';
      ctx.lineWidth   = 1.5;
      ctx.stroke();

      ctx.restore();
    }

    // ── Phase 1: Dot grid drifting inward ────────────────────────────────────
    if (phase >= 1) {
      const dotProgress = Math.min(1, (elapsed - 2.0) / 1.0);
      const alpha = dotProgress * (phase >= 4 ? 0 : 0.55);

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle   = 'rgba(180,210,255,0.8)';

      const gridW = 12, gridH = 8;
      for (let row = 0; row < gridH; row++) {
        for (let col = 0; col < gridW; col++) {
          // Start from corners, drift inward
          const normX = col / (gridW - 1);
          const normY = row / (gridH - 1);
          // Source: top corners (0,0) and (1,0)
          const srcX = normX < 0.5 ? 0 : W;
          const srcY = 0;
          const dstX = cx + (normX - 0.5) * W * 0.55;
          const dstY = cy + (normY - 0.5) * H * 0.45;
          const px = srcX + (dstX - srcX) * dotProgress;
          const py = srcY + (dstY - srcY) * dotProgress;

          ctx.beginPath();
          ctx.arc(px, py, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // ── Phase 2: Language words fly in ───────────────────────────────────────
    if (phase >= 2) {
      const wordProgress = Math.min(1, (elapsed - 3.0) / 1.0);
      const words = ['ESPAÑOL', 'FRANÇAIS', '日本語', 'DEUTSCH', '中文', 'PORTUGUÊS'];
      const alpha = wordProgress * (phase >= 4 ? 0 : 0.7);

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.font        = `bold ${Math.round(W * 0.022)}px sans-serif`;
      ctx.fillStyle   = 'rgba(120,180,255,1)';
      ctx.textAlign   = 'center';

      words.forEach((w, i) => {
        const angle  = (i / words.length) * Math.PI * 2;
        const srcR   = Math.max(W, H) * 0.8;
        const dstR   = Math.min(W, H) * 0.3;
        const r      = srcR - (srcR - dstR) * wordProgress;
        const px     = cx + Math.cos(angle) * r;
        const py     = cy + Math.sin(angle) * r * 0.65;
        ctx.fillText(w, px, py);
      });
      ctx.restore();
    }

    // ── Phase 3: Particle flood ───────────────────────────────────────────────
    if (phase >= 3) {
      if (st.particles.length === 0) {
        st.particles = initParticles(W, H);
      }

      const alpha = phase >= 4 ? 0 : 0.75;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle   = 'rgba(140,200,255,0.9)';

      st.particles.forEach(p => {
        if (p.done) return;
        const dx = cx - p.x;
        const dy = cy - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 6) {
          p.done = true;
          return;
        }
        const nx = dx / dist;
        const ny = dy / dist;
        p.x += nx * p.speed;
        p.y += ny * p.speed;

        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    // ── Phase 4: White flash ──────────────────────────────────────────────────
    if (phase === 4) {
      const flashElapsed = elapsed - 4.8;
      let flashAlpha;
      if (flashElapsed < 0.3) {
        flashAlpha = flashElapsed / 0.3;
      } else {
        flashAlpha = Math.max(0, 1 - (flashElapsed - 0.3) / 0.5);
      }
      ctx.save();
      ctx.globalAlpha = flashAlpha;
      ctx.fillStyle   = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(draw);
  }, []);

  // Start animation when visible
  useEffect(() => {
    if (!visible) return;

    // Reset state
    stateRef.current = {
      phase: -1,
      startTime: null,
      particles: [],
      waveOffset: 0,
      canvasOpacity: 1,
    };

    // Resize canvas
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    canvas.width  = parent.offsetWidth;
    canvas.height = parent.offsetHeight;

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, draw]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── Cycling "Everywhere" label ───────────────────────────────────────────────

function EverywhereLabel({ visible }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setIdx(i => (i + 1) % EVERYWHERE_LABELS.length), 1500);
    return () => clearInterval(id);
  }, [visible]);

  return (
    <Box sx={{ height: 28, position: 'relative', overflow: 'hidden', mt: 2, mb: 4 }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35 }}
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              color: 'rgba(255,255,255,0.22)',
              textTransform: 'uppercase',
            }}
          >
            {EVERYWHERE_LABELS[idx]}
          </Typography>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}

// ─── Waveform bar grid ────────────────────────────────────────────────────────

function WaveformBars({ visible }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        height: 80,
        mb: 6,
      }}
    >
      {BAR_HEIGHTS.map((h, i) => {
        const barH    = 14 + h * 66;
        const opacity = 0.07 + h * 0.11;
        return (
          <motion.div
            key={i}
            initial={{ scaleY: 0 }}
            animate={visible ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{
              duration: 0.55,
              delay: visible ? 5.4 + i * 0.012 : 0,
              ease: [0.16, 1, 0.3, 1],
            }}
            style={{
              flex: '1 1 0',
              maxWidth: 10,
              height: barH,
              background: `rgba(255,255,255,${opacity.toFixed(3)})`,
              borderRadius: 2,
              transformOrigin: 'center',
            }}
          />
        );
      })}
    </Box>
  );
}

// ─── Background orbit words ───────────────────────────────────────────────────

function OrbitWords() {
  return (
    <>
      {ORBIT_WORDS.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{
            opacity: item.opacity,
            y: [0, -10 + Math.sin(i) * 12, 0],
            x: [0, Math.cos(i * 0.7) * 8, 0],
          }}
          transition={{
            opacity: { duration: 1.2, delay: i * 0.15 },
            y: { duration: 6 + i * 0.4, repeat: Infinity, ease: 'easeInOut' },
            x: { duration: 7 + i * 0.3, repeat: Infinity, ease: 'easeInOut' },
          }}
          style={{
            position: 'absolute',
            left: item.x,
            top: item.y,
            pointerEvents: 'none',
            zIndex: 0,
            userSelect: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(0.65rem, 1.2vw, 0.9rem)',
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: '#fff',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
            }}
          >
            {item.word}
          </Typography>
        </motion.div>
      ))}
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function CinematicCTA() {
  const navigate     = useNavigate();
  const sectionRef   = useRef(null);
  const isInView     = useInView(sectionRef, { once: true, margin: '-15% 0px' });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInView && !visible) {
      setVisible(true);
    }
  }, [isInView, visible]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        position: 'relative',
        background: '#000',
        py: { xs: 20, md: 28 },
        overflow: 'hidden',
      }}
    >
      {/* Background orbit words */}
      <OrbitWords />

      {/* Top gradient rule */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: 1,
          background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.12) 30%, rgba(255,255,255,0.12) 70%, transparent)',
        }}
      />

      {/* Canvas convergence overlay */}
      <ConvergenceCanvas visible={visible} />

      {/* Main content */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* Waveform bars (callback to hero) */}
        <WaveformBars visible={visible} />

        {/* "Your voice." */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, filter: 'blur(20px)' }}
          animate={visible ? { opacity: 1, scale: 1, filter: 'blur(0px)' } : {}}
          transition={{ duration: 1.2, delay: 5.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(3rem, 9vw, 10rem)',
              fontWeight: 900,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '-0.05em',
              lineHeight: 0.9,
              mb: 0.5,
            }}
          >
            Your voice.
          </Typography>
        </motion.div>

        {/* "Everywhere." */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, filter: 'blur(20px)' }}
          animate={visible ? { opacity: 1, scale: 1, filter: 'blur(0px)' } : {}}
          transition={{ duration: 1.2, delay: 5.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(3rem, 9vw, 10rem)',
              fontWeight: 900,
              color: 'rgba(255,255,255,0.18)',
              textTransform: 'uppercase',
              letterSpacing: '-0.05em',
              lineHeight: 0.9,
            }}
          >
            Everywhere.
          </Typography>
        </motion.div>

        {/* Cycling language label */}
        <EverywhereLabel visible={visible} />

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 6.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/contact')}
            sx={{
              background: '#fff',
              color: '#000',
              fontWeight: 700,
              fontSize: '0.92rem',
              letterSpacing: '0.04em',
              px: 4,
              py: 1.6,
              borderRadius: 2,
              textTransform: 'uppercase',
              '&:hover': {
                background: 'rgba(255,255,255,0.88)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(255,255,255,0.18)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Get in Touch
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/demo')}
            sx={{
              borderColor: 'rgba(255,255,255,0.22)',
              color: 'rgba(255,255,255,0.75)',
              fontWeight: 600,
              fontSize: '0.92rem',
              letterSpacing: '0.04em',
              px: 4,
              py: 1.6,
              borderRadius: 2,
              textTransform: 'uppercase',
              backdropFilter: 'blur(12px)',
              background: 'rgba(255,255,255,0.04)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.08)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Watch demo
          </Button>
        </motion.div>

      </Container>

      {/* Bottom gradient fade */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 120,
          background: 'linear-gradient(to bottom, transparent, #000)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    </Box>
  );
}
